'use client'
import { type ReactNode, memo, useMemo, useState } from 'react'
import styled from 'styled-components'
import { motion } from 'motion/react'
import { useDropdown } from './useDropdown'
import { Figure } from '@/components'
import { getP15_2 , getTransition, mq, vw, hideScrollbar } from '../../styles'

const DropdownWrapper = styled.div<any>`
  position: relative;
  right: 0;

  ${mq.lessThan('tablet')} {
    width: ${vw(39, 'mobile')};
  }

  ${mq.greaterThan('tablet')} {
    width: ${vw(39, 'desktop')};
  }

  ${mq.greaterThan('container')} {
    width: 30.83px;
  }

  .triggerButton {
    ${getP15_2()}
    align-items: center;
    color: var(--color-white);
    display: flex;
    flex-direction: row;
    padding: 0;

    @media screen and (min-width: 768px) and (max-width: 1170px) {
      font-size: 13px;
    }

    ${mq.greaterThan('container')} {
      font-size: 15px;
    }

    span {
      color: var(--color-white);
      position: relative;
    }

    figure  {
      transform: ${({ $expanded, $direction }) => $expanded && $direction === 'down' ? 'rotate(-180deg)' : $direction === 'up' && !$expanded ? 'rotate(180deg)':  'none'};
      height: ${vw(15, 'mobile')};
      margin: 0 0 0 ${vw(5, 'mobile')};
      width:${vw(15, 'mobile')};


      ${mq.greaterThan('tablet')} {
        height: ${vw(20, 'desktop')};
        margin: 0 0 0 ${vw(5, 'desktop')};
        width: ${vw(20, 'desktop')};
      }

      ${mq.greaterThan('container')} {
        height: 12.7px;
        margin: 0 0 0 5px;
        width: 12.7px;
      }
    }
  }
  ul {
    bottom: ${({ $direction }) => $direction === 'up' ? '200%':  'unset'};
    top: ${({ $direction }) => $direction === 'down' ? '160%':  'unset'};
  }
`

const OptionsList = styled(motion.ul)<any>`
  height: 0;
  opacity: 0;
  overflow-y: auto;
  position: absolute;
  width: auto;
  z-index: 10;
  ${hideScrollbar()}

  li {
    cursor: pointer;
    opacity: .4;
    width: 100%;
    transition: opacity 300ms ease, background-color 300ms ease;
    ${getP15_2()}

    ${mq.lessThan('tablet')} {
      margin-bottom: ${vw(15, 'mobile')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(10, 'desktop')};
    }

    ${mq.greaterThan('container')} {
      margin-bottom: 10px;
    }

    &:last-of-type {
      margin-bottom: 0;
    }

    &:active,
    &:hover,
    &.active {
      opacity: 1;
    }

    button {
      ${getP15_2()}
      color: var(--color-white);
    }
  }
`

const dropDrownIcon = {
  type: 'svg' as const,
  src: '/images/svg/i--dropdown-arrow.svg',
  alt: 'dropdown icon',
  size: { width: 11, height: 5 }
}

const optionsListVariants = {
  collapsed: {
    height: '0',
    opacity: 0,
    transitionEnd: {
      display: 'none',
    },
    transition: { ...getTransition() }
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    display: 'block',
    transition: { ...getTransition() }
  }
}

interface IDropdown {
  className?: string,
  trigger: ReactNode | string,
  options: any,
  direction: string
}

const getOptions = (options, onClickOption, currentSelection) => {
  return options.map((option, idx) => (
    <li key={`${idx}-${option.value}`} className={option.value === currentSelection ? 'active' : ''}>
      <a href={option.path} onClick={onClickOption} >{option.value}</a>
    </li>
  ))
}

export const Dropdown = memo<IDropdown>(({ className = '', trigger, options, direction }) => {
  const [triggerRef, dropdownRef, expanded, toggleExpanded] = useDropdown()
  const [currentSelection, setSelection] = useState(options.find(options => options.selected))

  const onClickOption = (option) => {
    toggleExpanded()
    setSelection(option.value)
  }

  const Options = useMemo(() => getOptions(options, onClickOption, currentSelection), [options, currentSelection])

  return (
    <DropdownWrapper className={className} $expanded={expanded} $direction={direction}>
      <button className='triggerButton' type='button' ref={triggerRef}  onClick={toggleExpanded}>
        <span>{trigger}</span>
        <Figure media={dropDrownIcon} />
      </button>
      <OptionsList ref={dropdownRef} initial='collapsed' variants={optionsListVariants} animate={expanded ? 'expanded' : 'collapsed'}>
        {Options}
      </OptionsList>
    </DropdownWrapper>
  )
})
